import { ICommentary, ICommentaryLayoutRow } from '../interfaces/commentary'

/** A row holds 1-3 boxes: 1 = full width, 2 = halves, 3 = thirds. */
export const MAX_COMMENTARY_BOXES_PER_ROW = 3

/** Tab-wide arrangement: rows per zone (null key = the general area). */
export interface ITabLayoutModel {
  general: ICommentary[][]
  byExhibit: Record<string, ICommentary[][]>
}

/** Where a dragged box was dropped. */
export type IDropTarget =
  | { type: 'own-row'; zone: string | null; beforeRow: number }
  | { type: 'beside'; zone: string | null; targetId: string; side: 'left' | 'right' }

const byDragOrder = (a: ICommentary, b: ICommentary) =>
  (a.note?.dragOrder ?? Infinity) - (b.note?.dragOrder ?? Infinity)

export const hasSavedLayout = (commentaries: ICommentary[]): boolean =>
  commentaries.some((c) => c.layoutRow !== null && c.layoutRow !== undefined)

/**
 * Builds the arranged-mode layout model from the saved columns: boxes group
 * by layoutExhibit zone, then by layoutRow, ordered by dragOrder. Boxes with
 * no saved assignment (added after the tab was arranged, possibly by another
 * user or Arc) append after the arranged rows of their default zone
 * (section.exhibit), each on its own row, so nothing ever disappears.
 *
 * Row widths are relative to the zone's container: a box alone under a
 * column-constrained exhibit (e.g. the Collateral Overview top charts) fills
 * that column, not the page.
 */
export const buildArrangedModel = (commentaries: ICommentary[]): ITabLayoutModel => {
  const model: ITabLayoutModel = { general: [], byExhibit: {} }
  const rowsByZone = new Map<string | null, Map<number, ICommentary[]>>()
  const unarranged: ICommentary[] = []

  for (const commentary of [...commentaries].sort(byDragOrder)) {
    if (commentary.layoutRow === null || commentary.layoutRow === undefined) {
      unarranged.push(commentary)
      continue
    }
    const zone = commentary.layoutExhibit ?? null
    const zoneRows = rowsByZone.get(zone) ?? new Map<number, ICommentary[]>()
    const row = zoneRows.get(commentary.layoutRow) ?? []
    row.push(commentary)
    zoneRows.set(commentary.layoutRow, row)
    rowsByZone.set(zone, zoneRows)
  }

  for (const [zone, zoneRows] of rowsByZone) {
    const ordered = [...zoneRows.entries()].sort(([a], [b]) => a - b).map(([, row]) => row)
    if (zone === null) {
      model.general = ordered
    } else {
      model.byExhibit[zone] = ordered
    }
  }

  for (const commentary of unarranged) {
    const zone = commentary.section?.exhibit ?? null
    if (zone === null) {
      model.general.push([commentary])
    } else {
      model.byExhibit[zone] = [...(model.byExhibit[zone] ?? []), [commentary]]
    }
  }

  return model
}

/**
 * Guarantees the dragged box exists in the model applyMove will mutate.
 * Legacy custom slots (the beside-chart commentaries) render OUTSIDE the
 * zone grid, so the captured model may not contain them — seat the box in a
 * temporary trailing general row; applyMove removes it and inserts at the
 * real target.
 */
export const ensureBoxInModel = (model: ITabLayoutModel, box: ICommentary): ITabLayoutModel => {
  const allBoxes = [...model.general, ...Object.values(model.byExhibit).flat()].flat()
  if (allBoxes.some((c) => c.id === box.id)) return model
  return { ...model, general: [...model.general, [box]] }
}

const withoutBox = (rows: ICommentary[][], id: string): ICommentary[][] =>
  rows.map((row) => row.filter((c) => c.id !== id)).filter((row) => row.length > 0)

const cloneModel = (model: ITabLayoutModel): ITabLayoutModel => ({
  general: model.general.map((row) => [...row]),
  byExhibit: Object.fromEntries(
    Object.entries(model.byExhibit).map(([zone, rows]) => [zone, rows.map((row) => [...row])]),
  ),
})

const zoneRowsOf = (model: ITabLayoutModel, zone: string | null): ICommentary[][] =>
  zone === null ? model.general : (model.byExhibit[zone] ?? [])

const setZoneRows = (model: ITabLayoutModel, zone: string | null, rows: ICommentary[][]) => {
  if (zone === null) {
    model.general = rows
  } else {
    model.byExhibit[zone] = rows
  }
}

/**
 * Applies a drop to the model: removes the dragged box from wherever it is
 * and inserts it at the target. Returns null for no-op drops (e.g. dropping
 * a box beside itself) so callers can skip the save. Pure and UI-free so the
 * server jest suite can regression-test the movement math.
 */
export const applyMove = (
  model: ITabLayoutModel,
  draggedId: string,
  target: IDropTarget,
): ITabLayoutModel | null => {
  const allBoxes = [...model.general, ...Object.values(model.byExhibit).flat()].flat()
  const dragged = allBoxes.find((c) => c.id === draggedId)
  if (!dragged) return null
  if (target.type === 'beside' && target.targetId === draggedId) return null

  const next = cloneModel(model)
  setZoneRows(next, null, withoutBox(next.general, draggedId))
  for (const zone of Object.keys(next.byExhibit)) {
    setZoneRows(next, zone, withoutBox(next.byExhibit[zone], draggedId))
  }

  const rows = zoneRowsOf(next, target.zone)

  if (target.type === 'own-row') {
    // beforeRow indexes the PRE-removal row list. If the dragged box sat
    // ALONE in a row of this same zone ABOVE the strip, removing it shifted
    // every later row up by one — decrement so the box lands exactly where
    // the drop indicator showed. Cross-zone moves and multi-box source rows
    // don't collapse a row, so they don't shift. Still clamp: the strip
    // after the very last row can otherwise point past the end.
    const preRows = zoneRowsOf(model, target.zone)
    const sourceRowIndex = preRows.findIndex((row) => row.some((c) => c.id === draggedId))
    const sourceRowCollapsed =
      sourceRowIndex !== -1 &&
      preRows[sourceRowIndex].length === 1 &&
      sourceRowIndex < target.beforeRow
    const index = Math.min(target.beforeRow - (sourceRowCollapsed ? 1 : 0), rows.length)
    rows.splice(index, 0, [dragged])
    setZoneRows(next, target.zone, rows)
    return next
  }

  const rowIndex = rows.findIndex((row) => row.some((c) => c.id === target.targetId))
  if (rowIndex === -1) return null
  const row = rows[rowIndex]

  // Per spec: dropping beside a row that's already full isn't allowed in
  // place — the box lands on the next row instead. (When the dragged box
  // came FROM this row it was already removed above, so in-row reorders
  // never hit this branch.)
  if (row.length >= MAX_COMMENTARY_BOXES_PER_ROW) {
    rows.splice(rowIndex + 1, 0, [dragged])
    setZoneRows(next, target.zone, rows)
    return next
  }

  const targetIndex = row.findIndex((c) => c.id === target.targetId)
  row.splice(target.side === 'left' ? targetIndex : targetIndex + 1, 0, dragged)
  setZoneRows(next, target.zone, rows)
  return next
}

export interface ICommentaryLayoutAssignment {
  /** Zero-based row index within the box's zone. */
  layoutRow: number
  /** Exhibit key the box renders under; null = the tab's general area. */
  layoutExhibit: string | null
  /** Global 1-based sequence across the tab (persisted as notes.dragOrder). */
  dragOrder: number
}

/**
 * Flattens a visual arrangement (rows in visual order, each row belonging to
 * a zone) into per-commentary layout assignments. Shared by the SaveLayout
 * service (persists the assignments) and the client reducer (applies them
 * optimistically on drop) so both walks can never disagree.
 */
export const buildLayoutAssignments = (
  rows: ICommentaryLayoutRow[],
): Map<string, ICommentaryLayoutAssignment> => {
  const assignments = new Map<string, ICommentaryLayoutAssignment>()
  const nextRowIndexByZone = new Map<string | null, number>()
  let dragOrder = 0

  for (const row of rows) {
    const zone = row.exhibit ?? null
    const rowIndex = nextRowIndexByZone.get(zone) ?? 0
    nextRowIndexByZone.set(zone, rowIndex + 1)

    for (const id of row.boxes) {
      dragOrder += 1
      assignments.set(id, { layoutRow: rowIndex, layoutExhibit: zone, dragOrder })
    }
  }

  return assignments
}

// ─── Per-section groups (Loan Committee Report) ──────────────────────────────
//
// The LCR renders each tab's boxes grouped under their commentary_section, as
// collapsible groups. Nothing new is persisted for that: a group is a VIEW of
// the zone rows above filtered to one section, and a save re-emits every row of
// the tab in section order, so rows never mix sections once arranged.

/** The slice of a commentary_section the grouped layout needs. */
export interface ICommentarySectionRef {
  id: string
  exhibit?: string | null
  order?: number
}

/**
 * Key a commentary section's Not Applicable flag is stored under, in the same
 * `general_analysis_exhibits` table the chart/table exhibits use — that column
 * is free text, so the third level needs no schema of its own.
 *
 * Keyed by ID, not by name: a section renamed in UW Settings would otherwise
 * silently drop its flag. The prefix keeps these from ever colliding with a
 * real exhibit key (`Company Summary`, `Sales By SKU`, …).
 */
export const commentarySectionNaKey = (sectionId: string): string => `section:${sectionId}`

/** Zone a section's boxes render in: its exhibit, or the tab's general area. */
export const commentarySectionZone = (section: ICommentarySectionRef): string | null =>
  section.exhibit || null

/** Legacy (never arranged) rows pair boxes two-up, the odd last one full width. */
export const COMMENTARY_LEGACY_ROW_SIZE = 2

const chunk = <T>(items: T[], size: number): T[][] => {
  const rows: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    rows.push(items.slice(i, i + size))
  }
  return rows
}

const bySectionOrder = (a: ICommentarySectionRef, b: ICommentarySectionRef) =>
  (a.order ?? 0) - (b.order ?? 0)

/**
 * Rows of ONE section's boxes. Arranged boxes keep the row grouping they were
 * saved with (same layoutRow → same row, dragOrder within it) and boxes never
 * arranged append after them, each on its own row — the same walk
 * buildArrangedModel does for a zone, restricted to the section. A section with
 * no arranged box at all shows the legacy pairs.
 */
export const buildSectionRows = (boxes: ICommentary[]): ICommentary[][] => {
  const sorted = [...boxes].sort(byDragOrder)
  if (!hasSavedLayout(sorted)) return chunk(sorted, COMMENTARY_LEGACY_ROW_SIZE)
  const isArranged = (c: ICommentary) => c.layoutRow !== null && c.layoutRow !== undefined
  // Only the arranged boxes go through the zone/row grouping — a section's
  // boxes normally share one zone, but a stray assignment still keeps its row.
  const model = buildArrangedModel(sorted.filter(isArranged))
  const arrangedRows = [...model.general, ...Object.values(model.byExhibit).flat()]
  const unarrangedRows = sorted.filter((c) => !isArranged(c)).map((c) => [c])
  return [...arrangedRows, ...unarrangedRows]
}

/**
 * Serializes a tab arranged as per-section groups into save-layout rows. Zones
 * follow the sections' order (general area first); within a zone each section's
 * rows follow its `order`, so on read-back (buildArrangedModel → buildSectionRows)
 * every group keeps the rows it was saved with and `dragOrder` matches reading
 * order. `movedRows` replaces the rows of the section a drop just re-arranged.
 * Boxes of the tab that belong to none of the given sections are appended to
 * their zone on their own row, so a save can never drop them from the layout.
 */
export const buildGroupedLayoutRows = (params: {
  commentaries: ICommentary[]
  sections: ICommentarySectionRef[]
  movedSectionId?: string
  movedRows?: ICommentary[][]
}): ICommentaryLayoutRow[] => {
  const { commentaries, movedSectionId, movedRows } = params
  const sections = [...params.sections].sort(bySectionOrder)

  const boxesBySection = new Map<string, ICommentary[]>()
  for (const commentary of commentaries) {
    const list = boxesBySection.get(commentary.sectionId) ?? []
    list.push(commentary)
    boxesBySection.set(commentary.sectionId, list)
  }

  const zones: (string | null)[] = [null]
  for (const section of sections) {
    const zone = commentarySectionZone(section)
    if (!zones.includes(zone)) zones.push(zone)
  }

  const rows: ICommentaryLayoutRow[] = []
  const placed = new Set<string>()
  for (const zone of zones) {
    for (const section of sections) {
      if (commentarySectionZone(section) !== zone) continue
      const sectionRows =
        section.id === movedSectionId && movedRows
          ? movedRows
          : buildSectionRows(boxesBySection.get(section.id) ?? [])
      for (const row of sectionRows) {
        if (row.length === 0) continue
        rows.push({ exhibit: zone, boxes: row.map((c) => c.id) })
        row.forEach((c) => placed.add(c.id))
      }
    }
  }

  for (const commentary of [...commentaries].sort(byDragOrder)) {
    if (placed.has(commentary.id)) continue
    rows.push({
      exhibit: commentary.layoutExhibit ?? commentary.section?.exhibit ?? null,
      boxes: [commentary.id],
    })
  }

  return rows
}
